import axios from 'axios';
import {HttpUtils} from "@/api/common/HttpUtils";
import {UserFilter} from "@/api/modules/userManagement/UserFilter";
import {Convert} from "@/api/common/Convert";
import {User} from "@/api/modules/userManagement/models/User";
import {MemoryCache} from "@/api/common/MemoryCache";
import {Role} from "@/api/modules/userManagement/models/Role";
import {Company} from "@/api/modules/userManagement/models/Company";
import {Branch} from "@/api/modules/userManagement/models/Branch";


/*
   Access API end point for managing user.
   This class is intented only to be used within the *userManagement* modules.
 */
export class UserService {

    constructor(auth){

        if (auth == null){
            throw new Error("[e956965-ADM] UserService Error - Auth is missing!. ");
        }

        this._rootUrl = process.env.VUE_APP_API_URL;
        this._auth = auth;
        this._baseUrl = `${this._rootUrl}/v1/accounts`;
        this._baseUsersUrl = `${this._rootUrl}/v1/accounts/users`;
        this._headers = HttpUtils.getHttpHeaders(this._auth, 'text/json');
    }

    /**
     * getUsers gets a list of user that match the filter
     * @param filter
     * @returns {Promise<User>} if succcesful it will return a list of User objects.
     *
     */
    getUsers(filter){

        return new Promise((resolve, reject) => {

            if (filter != null){
                if ( (!filter instanceof UserFilter)) {
                    throw new Error ("[e83812] filter must be an instance of UserFilter type");
                }
            }

            let url = `${this._baseUsersUrl}/list?${this.__createFilterQuery(filter)}`;

            HttpUtils.get(url, this._auth).then(data => {

                if (data == null){
                    // 404 error - it should not happen unless the API URL is wrong.
                    throw new Error("[e268326-ADM] Unexpected result. Api endpoint might be wrong. possible 404 error.");
                }

                if (data == true){
                    // no results.
                    resolve([]);
                    return;
                }

                let users = [];

                let vm = this;
                data.forEach(function(obj){
                    let user = vm._parseResponseToUser(obj);
                    users.push(user);
                });

                resolve(users);

            }).catch( e => {

                window.tracer.error("[e471241-ADM] error when getting list of users")
                if (e != null) {
                    window.tracer.error(e);
                }
                reject(e);
            });

        });

    }

    /**
     * getUserRoles Gets the roles for a specific user.
     * @param userId
     * @returns {Promise<Role[]>}
     */
    getUserRoles(userId){
        return new Promise((resolve, reject) => {
            try{

                let url = `${this._baseUsersUrl}/${userId}/roles`;

                HttpUtils.get(url, this._auth).then(data => {

                    if (data == null){
                        resolve([]);
                        return;
                    }

                    if (data == true){
                        // no results.
                        resolve([]);
                        return;
                    }

                    let roles = [];

                    let vm = this;
                    data.forEach(function(obj){
                        let r = vm._parseResponseToRole(obj);
                        roles.push(r);
                    });

                    resolve(roles);

                }).catch(e => {
                    window.tracer.error(`[e699367-ADM] Server error when retrieving roles for ${userId}`)
                    if (e != null) {
                        window.tracer.error(e);
                    }
                    reject(e);
                });
            }
            catch(e){

                window.tracer.error(`[e231839-ADM] Error retrieving roles for ${userId} `)
                reject(e);
            }

        });
    }


    /**
     * search accounts. This is meant to be used with the autocompletion
     * @param searchString
     * @returns {Promise<string[]>}
     */
    searchAccounts(searchString){
        return new Promise((resolve, reject) => {

            if (searchString == null || searchString.length === 0){
                resolve([]);
                return;
            }

            let url = `${this._baseUrl}/search?q=${searchString}`;

            if (MemoryCache.instance.contains(url)){
                resolve(MemoryCache.instance.get(url));
                window.tracer.debug(`[d452899-ADM] Account list retrieved from cache for key ${url}`)
                return;
            }

            HttpUtils.get(url, this._auth).then(data => {

                if (data == null){
                    MemoryCache.instance.set(url, []);
                    resolve([]);
                    return;
                }

                if (data == true){
                    MemoryCache.instance.set(url, []);
                    resolve([]);
                    return;
                }

                if (data.length > 0){
                    MemoryCache.instance.set(url, data);
                    resolve(data);
                }
                else{
                    MemoryCache.instance.set(url, []);
                    resolve([]);
                }

            }).catch(e => {
                window.tracer.error("[e689715-ADM] Error while query server for accounts.")
                if (e != null) {
                    window.tracer.error(e);
                }

                reject(e);
            });

        });
    }


    /**
     * Creates a new user.
     * @param user
     * @returns {Promise<ustring>} The user id.
     */
    createUser(user, pwd) {
        return new Promise((resolve, reject) => {

            if (user == null){
                throw new Error("[e193000-ADM] User is not defined. Cannot create a new user.")
            }

            if ((!user instanceof User)){
                throw new Error("[e980517-ADM] Expected type of User")
            }


            let url = `${this._baseUsersUrl}/user`;

            let requestData = {
                account: user.account,
                username: user.userName,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                password: pwd
            };

            HttpUtils.post(url, this._auth, requestData).then(data => {

                if (data == null){
                    // 404 error - it should not happen unless the API URL is wrong.
                    throw new Error("[e819481-ADM] Unexpected response. Api endpoint might be wrong. possible 404 error.");
                }

                if (data == true || data.userId == null){
                    throw new Error("[e568787-ADM] Unexpected response. Expected the userId of the newly created user.")
                }

                resolve(data.userId);
            }).catch(e => {

                window.tracer.error("[e70612] Server error when creating a new user.")
                if (e != null) {
                    window.tracer.error(e);
                }

               reject(e);
            });

        });
    }

    /**
     * Update the users' account, email, first and last name. <br>
     * __Note:__ Currently we are restricting one account per user.
     * @param user
     */
    updateUser(user){

        if (user == null){
            throw new Error("[e527373-ADM] User is not defined. Cannot create a new user")
        }

        if ((!user instanceof User)){
            throw new Error("[e147537-ADM] Expected type of User")
        }

        return new Promise((resolve, reject) => {

            try {
                let url = `${this._baseUsersUrl}/${user.userId}`;


                let requestData = {
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    account: '',
                };

                if (user.account != null && user.account.length > 0) {
                    requestData.account = user.account;
                }

                HttpUtils.put(url, this._auth, requestData).then(() => {
                    resolve();
                }).catch(e => {

                    window.tracer.error("[e277934-ADM] Error while updating user on the server")
                    if (e != null) {
                        window.tracer.error(e);
                    }
                    reject(e);
                });

            }
            catch(e){

                window.tracer.error("[e672205-ADM] Error updating user");
                if (e != null) {
                    window.tracer.error(e);
                }
                reject(e);
            }
        });

    }

    changePassword(userId, password){
        return new Promise((resolve, reject) => {

            let url = `${this._baseUsersUrl}/${userId}/password`;

            let requestData = {
                password: password
            };

            HttpUtils.put(url, this._auth, requestData).then( (data) => {
                if (data == true || data == "OK") {
                    resolve(true);
                }
                else if (data === false)
                {
                    window.tracer.warn(`[w453827-ADM] Password has not been update`);
                    reject(null);
                }
                else if (data == null){
                    window.tracer.warn(`[w608265-ADM] User ${userId} not found. Password has not been update`);
                    reject(null);
                }
            }).catch( e => {

                window.tracer.error("[e277934-ADM] Server error while updating user's password.")
                if (e != null) {
                    window.tracer.error(e);
                }

                reject(e);
            });

        });

    }

    /**
     * Activates a user
     * @param userId
     * @returns {Promise<unknown>}
     */
    activateUser(userId){
        return this.__activateUser(userId, true);
    }

    /**
     * Deactivate a user
     * @param userId
     * @returns {Promise<unknown>}
     */
    deactivateUser(userId){
        return this.__activateUser(userId, false);
    }

    /**
     * __PRIVATE USE__  <br>
     * Activate or deactivate a user.
     * @param userId The user Id
     * @param activate True to activate user, false for deactivate
     * @returns {Promise<unknown>}
     * @private
     */
    __activateUser(userId, activate){

        if (activate == null){
            throw new Error("[e389374-ADM] activate is undefined");
        }

        let url = '';
        if (activate === true) {
            url = `${this._baseUsersUrl}/${userId}/activate`;
        }
        else{
            url = `${this._baseUsersUrl}/${userId}/deactivate`;
        }

        return new Promise((resolve, reject) => {

            HttpUtils.put(url, this._auth, null).then(data =>{
                if (data == false){
                    // user not found;
                    reject();
                }
                else {
                    resolve();
                }
            }).catch(e => {
                window.tracer.error("[e689785-ADM] Error activating an user");
                if (e != null) {
                    window.tracer.error(e);
                }
                reject(e);
            });

        });

    }



    /**
     * Add a role to a user.
     * @param userId
     * @param roleName
     * @returns {Promise<()>}
     */
    addRole(userId, roleName){
        let url = `${this._baseUsersUrl}/${userId}/roles/${roleName}`;

        return new Promise((resolve, reject) => {

            HttpUtils.put(url, this._auth, null).then(data => {
                if (data == true){
                    resolve();
                }
                else{
                    reject();
                }
            }).catch(e =>{

                window.tracer.error(`[e410560-ADM] Error adding role to user ${userId}`);
                if (e != null) {
                    window.tracer.error(e);
                }

                reject(e);
            });
        });

    }

    /**
     * Remove a role from a user. If roles is not assigned to the user, it will still return as successful.
     * @param userId
     * @param roleName
     * @returns {Promise<()>}
     */
    removeRole(userId, roleName){
        let url = `${this._baseUsersUrl}/${userId}/roles/${roleName}`;

        return new Promise((resolve, reject) => {

            HttpUtils.delete(url, this._auth).then(data => {
                if (data == true){
                    resolve();
                }
                else{
                    resolve();
                }
            }).catch(e =>{

                window.tracer.error(`[e879696-ADM] Error deleting role`);

                if (e != null) {
                    window.tracer.error(e);
                }

                reject(e);
            });
        });

    }


    isUsernameAvailable(username){

        return new Promise((resolve, reject) => {

            let url = `${this._baseUsersUrl}?username=${username}`;
            try{
                HttpUtils.head(url, this._auth).then(result => {
                     if (result === true){
                         // if exists, it will return 204, so, inverted
                         resolve(false);
                     }
                     else{
                         // if does not exits, then the server will return 404, then reverse it, to return that is available.
                         resolve(true);
                     }

                }).catch(e => {
                    window.tracer.error("[e473422-ADM] Server error while checking if a username exists on the server");
                    if (e != null) {
                        window.tracer.error(e);
                    }
                    reject(e);
                });
            }
            catch(e){

                window.tracer.error("[e610191-ADM] Error while checking if a username exists on the server");
                window.tracer.error(e);
                reject(e);
            }

        });
    }

    getCompanies(){
        let url = `${this._baseUrl}/companies`;

        return new Promise( (resolve, reject) => {

            if (MemoryCache.instance.contains(url)){
                resolve(MemoryCache.instance.get(url));
                return;
            }

           HttpUtils.get(url, this._auth).then(data => {

               if (data == null){
                   // 404 error - it should not happen unless the API URL is wrong.
                   throw new Error("[e600563-ADM] Unexpected response. Api endpoint might be wrong. possible 404 error.");
               }

               if (data == true){
                   // no results.
                   resolve([]);
                   return;
               }

               let companies = [];

               let vm = this;
               data.forEach(function(obj){
                   let c = vm._parseResponseToCompany(obj);

                   if (c != null) {
                       companies.push(c);
                   }
               });

               MemoryCache.instance.set(url, companies);
               resolve(companies);

           }).catch(e => {

               console.error('[e837704-ADM] Server Error getting list of companies');

               if (e != null) {
                   window.tracer.error(e);
               }

               reject(e);
           });
        });
    }



    getCompanyBranches(companyId){
        let url = `${this._baseUrl}/companies/${companyId}/branches`;
        return new Promise( (resolve, reject) => {
            HttpUtils.get(url, this._auth).then(data => {

                if (data == null){
                    // 404 error - it should not happen unless the API URL is wrong.
                    throw new Error("[e279848-ADM] Unexpected response. Api endpoint might be wrong. possible 404 error.");
                }

                if (data == true){
                    // no results.
                    resolve([]);
                    return;
                }


                let branches = [];

                let vm = this;
                data.forEach(function(obj){
                    let tmpBranch = vm._parseResponseToBranch(obj);

                    if (tmpBranch != null) {
                        branches.push(tmpBranch);
                    }
                });

                resolve(branches);


            }).catch(error => {
                if (error != null){
                    console.error('[e906231-ADM] server error when getting companies branches list.');
                    console.error(error);
                }
                reject(error);
            });
        });
    }

    /**
     * getRoles - Retrieve all the roles from the server and then it caches locally. There is an expectation that roles are rarely updated or created.
     * @returns {Promise<Role[]>}
     */
    getRoles(){

        return new Promise((resolve, reject) => {

            let url = `${this._baseUrl}/roles`;

            // roles does not change frequenly, so good idea to cache locally.

            if (MemoryCache.instance.contains(url)){
                resolve(MemoryCache.instance.get(url));
                return;
            }

            HttpUtils.get(url, this._auth).then(data => {

                if (data == null){
                    // 404 error - it should not happen unless the API URL is wrong.
                    throw new Error(`[e856550-ADM] Unexpected result. Api endpoint might be wrong. possible 404 error. url: ${url}`);
                }

                if (data == true){
                    // no results.
                    resolve([]);
                    return;
                }

                let roles = [];

                let vm = this;
                data.forEach(function(obj){
                    let role = vm._parseResponseToRole(obj);
                    roles.push(role);
                });

                MemoryCache.instance.set(url, roles);
                resolve(roles);

            }).catch( e => {

                window.tracer.error("[e471241-ADM] error when getting list of users")
                if (e != null) {
                    window.tracer.error(e);
                }
                reject(e);
            });

        });


    }

    __createFilterQuery(filter){

        let query = '';
        if (filter.account.length > 0) {
            query = query + `account=${filter.account}`;
        }

        if (filter.companyId.length > 0){
            query = query + (query.length > 0 ? "&" : "");
            query = query + `companyId=${filter.companyId}`;
            console.log('1: '+query);
        }

        if (filter.role.length > 0){
            query = query + (query.length > 0 ? "&" : "");
            query = query + `role=${filter.role}`;
        }

        return query;

    }

    _parseResponseToUser(resUser){

        if (resUser == null)return null;

        let user = new User();
        user.username = Convert.ToString(resUser.userName);
        user.account = Convert.ToString(resUser.account);
        user.userId = Convert.ToString(resUser.userId);
        user.firstName = Convert.ToString(resUser.firstName);
        user.lastName = Convert.ToString(resUser.lastName);
        user.companyId = Convert.ToString(resUser.companyId);
        user.companyName = Convert.ToString(resUser.companyName);
        user.isActive = Convert.ToBoolean(resUser.isActive);
        user.emailAddress = Convert.ToString(resUser.emailAddress);
        user.homeBranchId = Convert.ToString(resUser.homeBranchId);

        return user;
    }

    _parseResponseToRole(resRole){

        if (resRole == null)return null;

        let tmpId = Convert.ToString(resRole.id);
        let tmpName = Convert.ToString(resRole.name);
        let role = new Role(tmpId, tmpName);
        return role
    }

    _parseResponseToCompany(resCompany){

        if (resCompany == null)return null;

        let tmpId = Convert.ToString(resCompany.id);
        let tmpName = Convert.ToString(resCompany.name);
        let c = new Company(tmpId, tmpName);
        return c;
    }

    _parseResponseToBranch(resBranch){

        if (resBranch == null)return null;

        let tmpId = Convert.ToString(resBranch.id);
        let tmpName = Convert.ToString(resBranch.name);
        let branch = new Branch(tmpId, tmpName);
        return branch;
    }



}














